import { useCallback, useEffect, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { LifetimeProfit, LifetimeProfitRow, UserProfitArchive } from '../../api/adminTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { AdminNotice, EmailLookupForm } from './adminShared'
import { describeAdminError, useEmailParam } from './adminHelpers'
/**
 * Equivalent of legacy `AdminUserLifetimeProfitComponent` (route
 * `admin/statistics/profit/lifetime`). Two contract endpoints, both scoped
 * to one user:
 *  - #26 `.../profit/lifetime` — real, same shape as the user-facing B3
 *    `lifetimeProfit` (`LifetimeProfit`/`LifetimeProfitRow`);
 *  - #12 `/admin/statistics/profit/byUser` — ARCHIVE, a 30-day zero-history
 *    feed (the writer cron was disabled in legacy too) — shown collapsed by
 *    default since it is expected to be empty.
 */
export function AdminUserLifetimeProfitPage() {
  const [email, setEmail] = useEmailParam()
  const [lifetime, setLifetime] = useState<LifetimeProfit | null>(null)
  const [archive, setArchive] = useState<UserProfitArchive | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()
  const [showArchive, setShowArchive] = useState(false)

  const load = useCallback((targetEmail: string) => {
    setLifetime(null)
    setArchive(null)
    setFeedback(undefined)
    adminClient
      .lifetimeProfit(targetEmail)
      .then((result) => setLifetime(result))
      .catch((err) => setFeedback(describeAdminError(err)))
    adminClient
      .profitArchiveByUser(targetEmail)
      .then((result) => setArchive(result))
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    if (email) load(email)
    else {
      setLifetime(null)
      setArchive(null)
    }
  }, [email, load])

  const columns: Column<LifetimeProfitRow>[] = [
    { key: 'exchangeId', label: 'Exchange', align: 'left', sortValue: (r) => r.exchangeId },
    { key: 'coin2', label: 'Quote asset', align: 'left', sortValue: (r) => r.coin2 },
    { key: 'firstDay', label: 'First day', align: 'left', sortValue: (r) => r.firstDay },
    { key: 'profit', label: 'Profit', align: 'right', numeric: true, sortValue: (r) => r.profit, format: (_v, r) => formatNumberByCoin(r.profit, r.coin2), cellClass: (v) => (Number(v) < 0 ? 'portal-pnl-negative' : Number(v) > 0 ? 'portal-pnl-positive' : undefined) },
    { key: 'positiveProfit', label: 'Positive', align: 'right', numeric: true, sortValue: (r) => r.positiveProfit, format: (_v, r) => formatNumberByCoin(r.positiveProfit, r.coin2) },
    { key: 'loss', label: 'Loss', align: 'right', numeric: true, sortValue: (r) => r.loss, format: (_v, r) => formatNumberByCoin(r.loss, r.coin2) },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user lifetime profit</h1>
      </div>
      <div className="portal-card">
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <AdminNotice feedback={feedback} />
        {!email && <p className="portal-text-muted">Enter a user e-mail to view their lifetime profit.</p>}
        {email && lifetime === null && !feedback && <p>Loading…</p>}
        {email && lifetime !== null && (
          <>
            <h3>By exchange</h3>
            <div className="portal-table-scroll">
              <Table columns={columns} rows={lifetime.all} rowKey={(r, i) => `${r.exchangeId}-${i}`} />
            </div>
            <h3>By coin</h3>
            <div className="portal-table-scroll">
              <Table columns={columns} rows={lifetime.byCoin} rowKey={(r, i) => `${r.exchangeId}-${r.coin1}-${i}`} />
            </div>
          </>
        )}
        {email && archive && (
          <>
            <button type="button" onClick={() => setShowArchive((v) => !v)}>
              {showArchive ? 'hide' : 'show'} legacy 30-day archive (expected empty)
            </button>
            {showArchive && (
              <p className="portal-text-muted">
                Total: {archive.total.profit} {archive.interval}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
